import React from 'react';
import { InputNumber, Radio, Select } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { NTH_DAYS, NTH_ORDINALS, WEEKDAYS, Weekday } from '../../../services/smartBetaApi';
import { NTH_DAY_LABEL, NTH_LABEL, SmartBetaForm, WEEKDAY_LABEL, WEEKDAY_NAME } from '../smartBetaForm';
import { Card, CardTitle, DayCell, DayStrip, MonthCell, MonthGrid, Row } from '../styles';

interface Props {
  form: SmartBetaForm;
  onChange: (patch: Partial<SmartBetaForm>) => void;
  disabled?: boolean;
}

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const toggle = <T,>(list: T[], v: T): T[] => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

const ScheduleSection: React.FC<Props> = ({ form, onChange, disabled }) => (
  <Card aria-label="Rebalancing schedule">
    <CardTitle>
      <CalendarOutlined /> Rebalancing Schedule
    </CardTitle>
    <Row>
      Frequency:
      <Select
        value={form.frequency}
        onChange={(frequency) => onChange({ frequency })}
        disabled={disabled}
        style={{ width: 130 }}
        options={[
          { value: 'WEEKLY', label: 'Weekly' },
          { value: 'MONTHLY', label: 'Monthly' },
        ]}
      />
    </Row>

    {form.frequency === 'WEEKLY' ? (
      <>
        <Row>
          Every
          <InputNumber
            min={1}
            max={52}
            precision={0}
            value={form.weeks}
            onChange={(weeks) => onChange({ weeks })}
            disabled={disabled}
            placeholder="weeks"
            style={{ width: 90 }}
          />
          week(s) on
        </Row>
        <DayStrip role="group" aria-label="Weekdays">
          {WEEKDAYS.map((d: Weekday) => (
            <DayCell
              key={d}
              type="button"
              $on={form.weekdays.includes(d)}
              aria-pressed={form.weekdays.includes(d)}
              aria-label={WEEKDAY_NAME[d]}
              title={WEEKDAY_NAME[d]}
              disabled={disabled}
              onClick={() => onChange({ weekdays: toggle(form.weekdays, d) })}
            >
              {WEEKDAY_LABEL[d]}
            </DayCell>
          ))}
        </DayStrip>
      </>
    ) : (
      <>
        <Row>
          Every
          <InputNumber
            min={1}
            max={12}
            precision={0}
            value={form.months}
            onChange={(months) => onChange({ months })}
            disabled={disabled}
            placeholder="months"
            style={{ width: 90 }}
          />
          month(s)
        </Row>
        <Radio.Group
          value={form.monthlyMode}
          onChange={(e) => onChange({ monthlyMode: e.target.value })}
          disabled={disabled}
          style={{ marginBottom: 10 }}
        >
          <Radio value="DAYS">Each</Radio>
          <Radio value="NTH">On the</Radio>
        </Radio.Group>

        {form.monthlyMode === 'DAYS' ? (
          <MonthGrid role="group" aria-label="Days of the month">
            {DAYS.map((d) => (
              <MonthCell
                key={d}
                type="button"
                $on={form.monthDays.includes(d)}
                aria-pressed={form.monthDays.includes(d)}
                disabled={disabled}
                onClick={() => onChange({ monthDays: toggle(form.monthDays, d).sort((a, b) => a - b) })}
              >
                {d}
              </MonthCell>
            ))}
          </MonthGrid>
        ) : (
          <Row>
            On the
            <Select
              value={form.nth}
              onChange={(nth) => onChange({ nth })}
              disabled={disabled}
              style={{ width: 110 }}
              options={NTH_ORDINALS.map((n) => ({ value: n, label: NTH_LABEL[n] }))}
            />
            <Select
              value={form.nthDay}
              onChange={(nthDay) => onChange({ nthDay })}
              disabled={disabled}
              style={{ width: 130 }}
              options={NTH_DAYS.map((d) => ({ value: d, label: NTH_DAY_LABEL[d] }))}
            />
          </Row>
        )}
      </>
    )}
  </Card>
);

export default ScheduleSection;
